// ABOUTME: Form number field component with automatic type conversion
// ABOUTME: Extends CFieldText with type="number" and string-to-number conversion, validates invalid input

import * as React from "react"
import { Controller, type Control, type FieldValues, type Path, type RegisterOptions } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CFieldText, type CFieldTextProps } from "./CFieldText"

type CFieldNumberBaseProps = Omit<CFieldTextProps, "control" | "name" | "rules" | "register" | "type">

interface CFieldNumberProps<T extends FieldValues> extends CFieldNumberBaseProps {
  control: Control<T>
  name: Path<T>
  rules?: RegisterOptions<T, Path<T>>
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  /** Error message shown when non-numeric input is detected. Uses t("common.correctText") by default */
  invalidNumberMessage?: string
}

export const CFieldNumber = <T extends FieldValues>({
  control,
  name,
  rules,
  onBlur: onBlurProp,
  invalidNumberMessage,
  color,
  helperText,
  ...props
}: CFieldNumberProps<T>) => {
  const { t } = useTranslation()
  const errorMessage = invalidNumberMessage ?? t("common.correctText")

  const mergedRules: RegisterOptions<T, Path<T>> = {
    ...rules,
    validate: {
      ...(typeof rules?.validate === "object" ? rules.validate : {}),
      ...(typeof rules?.validate === "function" ? { custom: rules.validate } : {}),
      notNaN: (value: unknown) => {
        if (typeof value === "number" && Number.isNaN(value)) {
          return errorMessage
        }
        return true
      },
    },
  }

  // Determine if automatic error display should be used
  // If color or helperText are explicitly passed, use those instead of automatic
  const hasCustomColor = color !== undefined
  const hasCustomHelperText = helperText !== undefined

  return (
    <Controller
      control={control}
      name={name}
      rules={mergedRules}
      render={({ field, fieldState }) => (
        <CFieldText
          name={field.name}
          value={Number.isNaN(field.value) ? "" : (field.value ?? "")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const input = e.target
            if (input.validity.badInput) {
              field.onChange(NaN)
            } else {
              const val = input.value
              field.onChange(val === "" ? null : Number(val))
            }
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            field.onBlur()
            onBlurProp?.(e)
          }}
          type="number"
          color={hasCustomColor ? color : fieldState.error ? "failure" : undefined}
          helperText={hasCustomHelperText ? helperText : fieldState.error?.message}
          {...props}
        />
      )}
    />
  )
}

CFieldNumber.displayName = "CFieldNumber"
